import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useOutlet,
} from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { WIZARD_STEPS_ORDER } from './signupState'
import WizardIdentityStrip from './WizardIdentityStrip'

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1]
const EASE_OUT_EXPO_CSS = 'cubic-bezier(0.16, 1, 0.3, 1)'

/**
 * SetupWizardLayout — persistent shell for the 3-step post-signup wizard.
 *
 * IMPORTANT: this is a *route layout component*. It is mounted ONCE
 * by the parent route (`SignupRoutes`) and renders <Outlet /> for the
 * step body. Step pages must NOT wrap themselves in this layout —
 * they just return their inner form content and call
 * `useWizardStepConfig({ canContinue, onContinue, onSkipStep,
 * rightSlot })` to wire up the persistent footer + bar.
 *
 * Why persistent: the progress bar fill animates smoothly between
 * steps via a CSS width transition. That only fires when the same
 * DOM element gets a new style — i.e., the bar element must NOT
 * remount on step change. Same logic for the "Step X of 3" +
 * microcopy cross-fade, which would just snap to the new value if
 * the surrounding element re-mounted.
 *
 * The within-wizard step content swap (slide+fade) is owned here too
 * via an inner AnimatePresence keyed on `currentStep`.
 *
 * Props: none. Reads currentStep from useLocation().
 *
 * Exports:
 *   - default SetupWizardLayout (route element)
 *   - useWizardStepConfig({ canContinue, onContinue, onSkipStep,
 *       rightSlot? }) — call from step pages
 */

const STEP_META = {
  locations: { num: 1, microcopy: 'Just a few minutes.' },
  size:      { num: 2, microcopy: 'Almost done.' },
  policies:  { num: 3, microcopy: 'Last one.' },
}

const TOTAL_STEPS = WIZARD_STEPS_ORDER.length

// Map a wizard pathname → step id. Anything outside the known set
// falls back to the first step (defensive — keeps the bar at 20%
// instead of crashing if the route table ever drifts).
function pathnameToStep(pathname) {
  const last = pathname.split('/').filter(Boolean).pop()
  return STEP_META[last] ? last : WIZARD_STEPS_ORDER[0]
}

/* ─────────────── Step ↔ Shell context ─────────────── */

const WizardShellContext = createContext(null)

export function useWizardShell() {
  const value = useContext(WizardShellContext)
  if (!value) {
    throw new Error(
      'useWizardShell must be used inside a SetupWizardLayout route',
    )
  }
  return value
}

/**
 * Step pages call this once per render with their current config.
 * Handlers (onContinue/onSkipStep) are stored in a ref — they don't
 * need to be reference-stable, the shell always pulls the latest at
 * click time. Visual values (canContinue/rightSlot) flow through
 * state so the shell re-renders when they change.
 */
export function useWizardStepConfig(config) {
  const { setStepConfig } = useWizardShell()
  // useLayoutEffect so the shell sees this step's config BEFORE paint
  // when a new step mounts under the persistent shell. No deps array
  // → fires every render; setStepConfig short-circuits if nothing
  // visual changed, so this is cheap.
  useLayoutEffect(() => {
    setStepConfig(config)
  })
}

/* ─────────── Step body content swap (slide + fade) ───────────
   Choreography: 350ms slide+fade in, 200ms slide+fade out, ease-out-expo.
   Direction-aware: forward (next step) slides the new step in from the
   right and pushes the old step out to the left; back (prev step) is
   the mirror — new step in from the left, old step out to the right.

   Why direction-aware: going back from a tall step (e.g. policies)
   to a short step (e.g. size) without a clear opposite-direction exit
   reads as a "stutter" — the old tall content lingered fading on top
   of where the footer was sliding up to its new position, and both
   the exit fade and the new step's enter slide pulled the eye in the
   same direction. Mirroring the slide on Back makes the height-collapse
   feel like a deliberate transition instead of a glitch.

   mode="popLayout" (NOT "wait"): the exiting step is taken out of the
   normal layout flow (position: absolute, contained by the parent's
   `relative`) the moment the new step mounts. Otherwise the wrapper
   would hold the OLD step's height for the full 200ms exit, then
   collapse to the new (often much shorter) step's height in a single
   frame — and the footer below would snap up by hundreds of pixels
   mid-transition. With popLayout the exit and enter overlap as a brief
   cross-fade while the footer slides smoothly to its new position via
   its own layout="position" handler.

   IMPORTANT: `children` here MUST be a *captured* React element
   (already-resolved step component), NOT a `<Outlet />` element.
   Outlet re-resolves the matched route from live context on every
   render — meaning the OLD motion.div, kept alive during exit by
   AnimatePresence, would re-evaluate Outlet against the NEW URL and
   flash the new step's content mid-exit. The parent passes the
   `useOutlet()` return value (a stable element captured at this
   render's location) instead. */

const STEP_VARIANTS = {
  enter: (direction) => ({ opacity: 0, x: direction >= 0 ? 20 : -20 }),
  center: { opacity: 1, x: 0 },
  exit: (direction) => ({
    opacity: 0,
    x: direction >= 0 ? -20 : 20,
    transition: { duration: 0.2, ease: EASE_OUT_EXPO },
  }),
}

function StepBodySwap({ stepKey, children, direction }) {
  const reduceMotion = useReducedMotion()

  if (reduceMotion) {
    return <div key={stepKey}>{children}</div>
  }

  return (
    <AnimatePresence mode="popLayout" initial={false} custom={direction}>
      <motion.div
        key={stepKey}
        custom={direction}
        variants={STEP_VARIANTS}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.35, ease: EASE_OUT_EXPO }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}

/* ─────────── Step number + microcopy with fade ───────────
   Per spec: 200ms out → 200ms in (mode="wait", no overlap). The
   simpler-than-cross-fade fallback explicitly approved in the brief.
   Bar fill is the priority animation; this text just keeps the
   numbers feeling smooth. */

function FadeText({ stepKey, className, children, reduceMotion }) {
  if (reduceMotion) {
    return <span className={className}>{children}</span>
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={stepKey}
        className={className}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: EASE_OUT_EXPO }}
      >
        {children}
      </motion.span>
    </AnimatePresence>
  )
}

/* ─────────────────────────  Main layout  ───────────────────────── */

export default function SetupWizardLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const reduceMotion = useReducedMotion()

  // Resolve the matched child element ONCE per render and pass it
  // (rather than <Outlet />) into StepBodySwap. Captured elements
  // give the AnimatePresence exit phase the OLD step to render
  // instead of re-resolving Outlet against the NEW route context.
  const stepElement = useOutlet()

  const currentStep = pathnameToStep(location.pathname)
  const meta = STEP_META[currentStep] || STEP_META.locations
  const pct = (meta.num / TOTAL_STEPS) * 100

  const currentIdx = WIZARD_STEPS_ORDER.indexOf(currentStep)
  const prevStep = currentIdx > 0 ? WIZARD_STEPS_ORDER[currentIdx - 1] : null

  // Track navigation direction for the step swap animation.
  // The ref holds the PREVIOUS render's index, so during the render
  // where currentIdx changes we can compare them and pick a slide
  // direction. We update the ref in an effect AFTER paint so the
  // comparison stays valid for the current render. Initial render
  // has direction === 0; STEP_VARIANTS treats >=0 as forward, which
  // matches the "always slide from right on first paint" feel.
  const prevStepIdxRef = useRef(currentIdx)
  const direction =
    currentIdx > prevStepIdxRef.current
      ? 1
      : currentIdx < prevStepIdxRef.current
      ? -1
      : 0
  useEffect(() => {
    prevStepIdxRef.current = currentIdx
  }, [currentIdx])

  // Step config plumbing.
  // Handlers live in a ref — they update on every step-page render
  // without forcing the shell to re-render. Visual config lives in
  // state so the shell re-renders (and the Continue button enables
  // / accent repaints / right slot appears or disappears) when
  // something visible changes.
  const handlersRef = useRef({ onContinue: null, onSkipStep: null })
  const [visualCfg, setVisualCfg] = useState({
    canContinue: true,
    rightSlot: null,
  })

  const setStepConfig = useCallback((cfg) => {
    handlersRef.current.onContinue = cfg.onContinue ?? null
    handlersRef.current.onSkipStep = cfg.onSkipStep ?? null

    setVisualCfg((prev) => {
      const next = {
        canContinue: cfg.canContinue ?? true,
        rightSlot: cfg.rightSlot ?? null,
      }
      if (
        next.canContinue === prev.canContinue &&
        next.rightSlot === prev.rightSlot
      ) {
        return prev
      }
      return next
    })
  }, [])

  const ctxValue = useMemo(() => ({ setStepConfig }), [setStepConfig])

  const handleBack = () => {
    if (!prevStep) return
    navigate(`/signup/setup/${prevStep}`)
  }

  const handleContinue = () => {
    handlersRef.current.onContinue?.()
  }

  const handleSkipStep = () => {
    handlersRef.current.onSkipStep?.()
  }

  // Bar transition: 700ms width + bg-color animation, ease-out-expo.
  // We have to gate the inline duration on reduceMotion explicitly:
  // the className `motion-safe:transition-[width,background-color]`
  // drops transition-property under reduced motion, but inline
  // transitionDuration would still apply to the implicit `all`
  // default and animate every property — i.e. the opposite of what
  // we want. Under reduced motion we just clear duration so width +
  // color changes snap.
  const barTransitionStyle = reduceMotion
    ? { width: `${pct}%`, transitionDuration: '0ms' }
    : {
        width: `${pct}%`,
        transitionDuration: '700ms',
        transitionTimingFunction: EASE_OUT_EXPO_CSS,
      }

  // Shared button row — rendered into BOTH the mobile fixed footer
  // (outside the flow) and the desktop inline footer (inside the
  // content column). Responsive classes flip between mobile
  // flex-grow sizing and desktop fixed-width / ml-auto alignment,
  // so the same JSX serves both surfaces.
  const buttonRow = (
    <>
      <div className="flex items-center gap-3">
        {prevStep && (
          <button
            type="button"
            onClick={handleBack}
            className="
              inline-flex items-center justify-center gap-1 rounded-lg
              h-12 px-4 flex-1 text-[15px] text-[var(--signup-text-secondary)]
              md:flex-none md:h-[52px] md:px-6 md:text-[14px] md:text-[var(--signup-text-primary)]
              bg-transparent border border-[var(--signup-border-default)] font-medium
              hover:bg-[var(--signup-bg-subtle)] hover:border-[var(--signup-border-strong)] hover:text-[var(--signup-text-primary)]
              focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)]
              motion-safe:transition-colors motion-safe:duration-150 motion-safe:active:scale-[0.98]
            "
          >
            ← Back
          </button>
        )}
        <button
          type="button"
          onClick={handleContinue}
          disabled={!visualCfg.canContinue}
          className={`
            inline-flex items-center justify-center gap-1 rounded-lg
            h-12 text-[15px]
            md:h-[52px] md:w-[140px] md:ml-auto md:text-[14px]
            ${prevStep ? 'flex-[2]' : 'flex-1'} md:flex-none
            bg-[var(--signup-accent)] text-white font-medium
            hover:bg-[var(--signup-accent-hover)] disabled:opacity-40 disabled:cursor-not-allowed
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--signup-bg-base)]
            motion-safe:transition-[background-color,box-shadow,transform] motion-safe:duration-150 motion-safe:active:scale-[0.98]
          `}
          style={{ boxShadow: 'var(--signup-shadow-sm)' }}
          onMouseEnter={(e) => {
            if (e.currentTarget.disabled) return
            e.currentTarget.style.boxShadow = 'var(--signup-shadow-md)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = 'var(--signup-shadow-sm)'
          }}
        >
          Continue →
        </button>
      </div>

      <div className="mt-3.5 md:mt-4 text-center">
        <button
          type="button"
          onClick={handleSkipStep}
          className="text-[14px] md:text-[13px] text-[var(--signup-text-secondary)] md:text-[var(--signup-text-tertiary)] hover:text-[var(--signup-text-primary)] underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-border-strong)] rounded-sm px-1 py-0.5 motion-safe:transition-colors motion-safe:duration-150"
        >
          Skip this step
        </button>
      </div>
    </>
  )

  return (
    <WizardShellContext.Provider value={ctxValue}>
      {/* Root: document-level scroll on mobile. We deliberately do
          NOT trap the scroll inside a body container with
          overflow-y-auto — that pattern relies on a chain of
          flex-1/min-h-0/min-h-full that fights the browser's
          percentage-height rules on step 3 (the tallest step), and
          ends up clipping content instead of scrolling. Letting the
          document scroll natively + pinning the mobile footer with
          position: fixed mirrors the SignupCommunity layout, which
          is already battle-tested for iOS keyboard interactions
          (fixed elements pin to the visual viewport, so the footer
          stays above the keyboard automatically). svh keeps the
          minimum height stable through Safari's URL-bar collapse. */}
      <div
        className="min-h-screen min-h-[100svh] font-sans antialiased flex flex-col bg-[var(--signup-bg-base)] text-[var(--signup-text-primary)]"
        style={{
          fontFeatureSettings: '"cv11", "ss03", "cv05"',
          textRendering: 'optimizeLegibility',
        }}
      >
        {/* Header — 64px mobile / 80px desktop */}
        <header className="h-16 md:h-20 flex items-center px-4 sm:px-10 shrink-0">
          <Link
            to="/"
            aria-label="Statbox home"
            className="flex items-center gap-2"
          >
            <img
              src="/Logo_Statbox_Transparent.png"
              alt=""
              className="h-7 w-auto"
            />
            <img
              src="/logo_white.png"
              alt="Statbox"
              className="h-4 w-auto opacity-90"
              style={{ filter: 'invert(1)' }}
            />
          </Link>
        </header>

        {/* Wizard chrome — identity strip + progress bar as one tight
            unit directly below the header. The :first-child:not-
            :last-child override squashes WizardIdentityStrip's own
            mb-6 down to mb-2 (8px) when both elements are present;
            when the strip returns null (no community name yet) the
            override is suppressed so the progress bar sits flush. */}
        <div className="shrink-0 px-4 sm:px-10 mt-2 md:mt-4">
          <div className="mx-auto w-full max-w-[480px] md:max-w-[520px] [&>:first-child:not(:last-child)]:!mb-2">
            <WizardIdentityStrip />
            <div>
              <div
                className="h-[2px] w-full rounded-full bg-[var(--signup-bg-muted)] overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={TOTAL_STEPS}
                aria-valuenow={meta.num}
                aria-label={`Step ${meta.num} of ${TOTAL_STEPS}`}
              >
                <div
                  className="h-full rounded-full bg-[var(--signup-accent)] motion-safe:transition-[width,background-color]"
                  style={barTransitionStyle}
                />
              </div>
              <div className="mt-3 flex items-center justify-between text-[13px] tabular-nums">
                <FadeText
                  stepKey={`stepnum-${meta.num}`}
                  className="text-[var(--signup-text-secondary)]"
                  reduceMotion={reduceMotion}
                >
                  Step {meta.num} of {TOTAL_STEPS}
                </FadeText>
                <FadeText
                  stepKey={`micro-${meta.num}`}
                  className="italic text-[var(--signup-text-tertiary)]"
                  reduceMotion={reduceMotion}
                >
                  {meta.microcopy}
                </FadeText>
              </div>
            </div>
          </div>
        </div>

        {/* Content area — flex-1 fills the viewport's remaining
            height when content is short; grows past the viewport
            when content is long, at which point the document
            scrolls natively. pb-48 reserves ~192px below the step
            content for the mobile fixed footer (~150px tall on
            iPhone with safe-area inset, plus ~32px breathing room).
            md:pb-0 reverts the reservation on desktop where the
            footer is inline at the end of the column. */}
        <div className="flex-1 flex flex-col items-center px-4 sm:px-10 pt-6 md:pt-12 pb-48 md:pb-0">
          <div
            className={
              visualCfg.rightSlot
                ? 'w-full max-w-[1000px] grid grid-cols-1 lg:grid-cols-[520fr_360fr] gap-10 lg:gap-20 items-start'
                : 'w-full max-w-[480px] md:max-w-[520px]'
            }
          >
            <div>
              {/* Step body — animated swap, content from useOutlet().
                  `relative` contains the absolute-positioned exiting
                  step from popLayout's swap. */}
              <div className="relative">
                <StepBodySwap stepKey={currentStep} direction={direction}>
                  {stepElement}
                </StepBodySwap>
              </div>

              {/* Desktop-only inline footer. On mobile this is hidden
                  and the fixed footer below the root takes over.
                  layout="position" keeps the smooth vertical slide
                  when the step body changes height between steps. */}
              <motion.div
                layout={reduceMotion ? false : 'position'}
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : { duration: 0.35, ease: EASE_OUT_EXPO }
                }
                className="hidden md:block mt-12"
              >
                {buttonRow}
              </motion.div>
            </div>

            {/* Right column: optional preview slot (no current step
                uses this, but the API still supports it). */}
            {visualCfg.rightSlot && (
              <div className="lg:pt-[40px]">{visualCfg.rightSlot}</div>
            )}
          </div>
        </div>

        {/* Mobile-only fixed footer — outside the document flow so
            it doesn't fight the content area's flex layout. iOS
            Safari pins position:fixed to the *visual* viewport, so
            this footer automatically follows the keyboard above
            focused inputs. The pb-48 padding on the content area
            above reserves visual space so the last form field never
            sits underneath this footer. */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-10 px-5 pt-4 pb-[calc(1rem_+_env(safe-area-inset-bottom))] bg-[var(--signup-bg-base)] border-t border-[var(--signup-border-subtle)]">
          <div className="mx-auto w-full max-w-[480px]">{buttonRow}</div>
        </div>
      </div>
    </WizardShellContext.Provider>
  )
}
